import { useCallback, useEffect, useRef } from 'react';

import { callHoldNameFor, holdCall, ReleaseCall } from '@/app/callHold/callHold';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';

export default function useCallHold() {
  const name = callHoldNameFor(useMeetingData());
  const { isClient } = useUserData();
  const heldCall = useRef<ReleaseCall | null>(null);
  const pendingClaim = useRef<Promise<boolean> | null>(null);
  const isGone = useRef(false);

  const release = useCallback(() => {
    heldCall.current?.();
    heldCall.current = null;
    pendingClaim.current = null;
  }, []);

  const keep = useCallback((releaseCall: ReleaseCall | null) => {
    if (!releaseCall) {
      pendingClaim.current = null;
      return false;
    }
    if (isGone.current) {
      releaseCall();
      return false;
    }

    heldCall.current = releaseCall;
    return true;
  }, []);

  const claim = useCallback(() => {
    if (!isClient) {
      return Promise.resolve(true);
    }

    pendingClaim.current ??= holdCall(name).then(keep);
    return pendingClaim.current;
  }, [isClient, keep, name]);

  useEffect(function releaseWhenThePageGoes() {
    isGone.current = false;

    return () => {
      isGone.current = true;
      release();
    };
  }, [release]);

  return { claim, release };
}
